/** @jsxImportSource theme-ui */
import React, { useEffect, useState } from 'react';
import { SxProp } from 'theme-ui';

import { useDebouncedState } from 'hooks/useDebouncedState';

import { Typography } from 'components/_common/Typography';
import { AdditiveSearchCategoryMenu } from 'components/_controls/AoiSearch/AdditiveSearchCategoryMenu';
import { LocationSearchResult } from 'components/_controls/AoiSearch/models';

import BdInput from '../BdInput/BdInput';
import {
  mapResultToAdditiveCategories,
  MinLocationSearchLength,
  searchMenuWrapperStyles,
} from './locationSearch.helpers';

type BdAdditiveLocationSearchProps = SxProp & {
  selectedItemIds: number[];
  debounceDelay?: number;
  closeOnSelect?: boolean;
  placeholder?: string;
  isAddInProgress?: boolean;
  forceDisable?: boolean;
  hasMobileDropDownBoxShadow?: boolean;
  bottomBorderOnly?: boolean;
  error?: string;
  noResultsSubText?: string;
  noResultsHintText?: string;
  inputLabel?: string;
  onSearchApiCall: (text: string) => Promise<LocationSearchResult[]>;
  onAddItem?: (item: LocationSearchResult) => void;
};
export const BdAdditiveLocationSearch: React.FC<BdAdditiveLocationSearchProps> = ({
  selectedItemIds,
  debounceDelay = 500,
  closeOnSelect = false,
  placeholder = 'Add a location',
  isAddInProgress,
  forceDisable = false,
  hasMobileDropDownBoxShadow = false,
  bottomBorderOnly = false,
  error = null,
  noResultsSubText = null,
  noResultsHintText = null,
  inputLabel,
  onSearchApiCall,
  onAddItem,
  ...props
}) => {
  const [searchText, searchTextDebounced, setSearchText, setSearchTextForceNoProxy] =
    useDebouncedState('', debounceDelay);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState<LocationSearchResult>(null);

  const hasSearchText = searchText?.length > 0;
  const hasMatchingResults = hasSearchText && !filteredItems?.length;
  const noMatchingResults = hasSearchText && !isLoading && !hasMatchingResults;

  useEffect(() => {
    if (searchTextDebounced?.length >= MinLocationSearchLength) {
      setIsLoading(true);
      onSearchApiCall &&
        onSearchApiCall(searchTextDebounced)
          .then(data => {
            const mappedCategories = mapResultToAdditiveCategories(data, selectedItemIds);
            setFilteredItems(mappedCategories);
          })
          .finally(() => {
            setIsLoading(false);
          });
    } else if (searchTextDebounced?.length) {
      setFilteredItems([]);
    } else {
      setIsLoading(false);
    }
  }, [selectedItemIds, onSearchApiCall, searchTextDebounced]);

  const handleOnSelect = (item: LocationSearchResult) => {
    setSelectedItem(item);
    closeOnSelect && setSearchTextForceNoProxy('');
  };

  const handleOnAdd = () => {
    onAddItem && onAddItem(selectedItem);
    setSearchTextForceNoProxy('');
    setSelectedItem(null);
    setFilteredItems([]);
  };

  const handleOnCancel = () => {
    setSearchTextForceNoProxy('');
    setSelectedItem(null);
    setFilteredItems([]);
    setIsLoading(false);
  };

  const handleChange = (val: string) => {
    if (!val.length) {
      setSearchTextForceNoProxy('');
      setSelectedItem(null);
      setFilteredItems([]);
      setIsLoading(false);
    } else {
      setSearchText(val);
    }
  };

  return (
    <div sx={{ position: 'relative' }} {...props}>
      {!!inputLabel?.length && (
        <Typography variant="body2" color="stone70" marginBottom="8px">
          {inputLabel}
        </Typography>
      )}
      <BdInput
        value={searchText}
        iconName="icon-maximize"
        placeholder={placeholder}
        error={error}
        isLoading={isLoading}
        onChange={handleChange}
        bottomBorderOnly={bottomBorderOnly}
      />
      {(hasMatchingResults || noMatchingResults) && (
        <div
          className="additive-search-menu"
          sx={searchMenuWrapperStyles(hasMobileDropDownBoxShadow)}
        >
          <AdditiveSearchCategoryMenu<LocationSearchResult>
            categories={filteredItems}
            selectedId={selectedItem?.id}
            trayButtonsState={
              isAddInProgress ? 'busy' : !selectedItem || forceDisable ? 'disabled' : 'enabled'
            }
            onSelect={handleOnSelect}
            onCancel={handleOnCancel}
            onAdd={handleOnAdd}
            noResultsSubText={noResultsSubText}
            noResultsHintText={noResultsHintText}
          />
        </div>
      )}
    </div>
  );
};
